import { formatFileSize } from './file_size';
export { formatFileSize } from './file_size';
import { isImageArtifact } from '@/lib/imageArtifact';
import { isEditableDocumentFile } from './document_file_type';
export { isEditableDocumentFile } from './document_file_type';
import { createFileOpenGuard } from '@/lib/fileOpenSession';
import { mayNeedSpreadsheetImportRecovery } from '@/lib/spreadsheetImportRecovery';
import { StyleSheet, Platform, Linking, Dimensions } from 'react-native';
import Icon from '@/components/Icon';
import { Image } from 'expo-image';
import { useUIStore } from '@/store/ui';
import { useArtifactViewerStore } from '@/store/artifactViewer';
import { useToastStore } from '@/store/toast';
import { getCollapsedNavigationSidebarWidth, canFitContextSidebar, isArtifactSidebarType } from '@/constants/config';
import { isCodeFile } from '@/constants/utils/code_file';
import { buildVideoProject, fileBelongsToVideoProject, firstVideoProjectMedia, isGeneratedVideoProjectFile, isVideoMedia, isStudioVideo } from '@/constants/utils/video_project';
export { DESKTOP_EDITOR_SIDEBAR_TYPES, ARTIFACT_SIDEBAR_TYPES, isDesktopEditorSidebarType, isArtifactSidebarType } from '@/constants/config';
export { isCodeFile } from '@/constants/utils/code_file';
export { isSvgImageUrl, isSvgImageFile, isSvgImageSource, getImagePreviewUrl } from '@/constants/utils/svg_image';

// Common file type categories
const FILE_TYPES = {
  IMAGE: 'image',
  DOCUMENT: 'document',
  CODE: 'code',
  AUDIO: 'audio',
  VIDEO: 'video',
  CAD: 'cad',
  FILE: 'file'
};

// Mapping of extensions to their file types
const EXTENSION_MAP = {
  // Images
  'jpg': FILE_TYPES.IMAGE,
  'jpeg': FILE_TYPES.IMAGE,
  'png': FILE_TYPES.IMAGE,
  'gif': FILE_TYPES.IMAGE,
  'webp': FILE_TYPES.IMAGE,
  'svg': FILE_TYPES.IMAGE,
  'eps': FILE_TYPES.IMAGE,
  'ai': FILE_TYPES.IMAGE,
  'heic': FILE_TYPES.IMAGE,

  // Documents
  'pdf': FILE_TYPES.DOCUMENT,
  'doc': FILE_TYPES.DOCUMENT,
  'docx': FILE_TYPES.DOCUMENT,
  'xls': FILE_TYPES.DOCUMENT,
  'xlsx': FILE_TYPES.DOCUMENT,
  'ppt': FILE_TYPES.DOCUMENT,
  'pptx': FILE_TYPES.DOCUMENT,
  'csv': FILE_TYPES.DOCUMENT,
  'tsv': FILE_TYPES.DOCUMENT,
  'ods': FILE_TYPES.DOCUMENT,
  'md': FILE_TYPES.DOCUMENT,
  'markdown': FILE_TYPES.DOCUMENT,
  'epub': FILE_TYPES.DOCUMENT,
  'mxl': FILE_TYPES.DOCUMENT,

  // Code
  'txt': FILE_TYPES.CODE,
  'html': FILE_TYPES.CODE,
  'htm': FILE_TYPES.CODE,
  'json': FILE_TYPES.CODE,
  'js': FILE_TYPES.CODE,
  'jsx': FILE_TYPES.CODE,
  'ts': FILE_TYPES.CODE,
  'tsx': FILE_TYPES.CODE,
  'css': FILE_TYPES.CODE,
  'scss': FILE_TYPES.CODE,
  'sass': FILE_TYPES.CODE,
  'less': FILE_TYPES.CODE,
  'xml': FILE_TYPES.CODE,
  'musicxml': FILE_TYPES.CODE,
  'py': FILE_TYPES.CODE,
  'pyw': FILE_TYPES.CODE,
  'ipynb': FILE_TYPES.CODE,
  'rb': FILE_TYPES.CODE,
  'erb': FILE_TYPES.CODE,
  'gemfile': FILE_TYPES.CODE,
  'java': FILE_TYPES.CODE,
  'class': FILE_TYPES.CODE,
  'jar': FILE_TYPES.CODE,
  'c': FILE_TYPES.CODE,
  'h': FILE_TYPES.CODE,
  'cpp': FILE_TYPES.CODE,
  'hpp': FILE_TYPES.CODE,
  'cc': FILE_TYPES.CODE,
  'hh': FILE_TYPES.CODE,
  'cs': FILE_TYPES.CODE,
  'go': FILE_TYPES.CODE,
  'rs': FILE_TYPES.CODE,
  'swift': FILE_TYPES.CODE,
  'kt': FILE_TYPES.CODE,
  'kts': FILE_TYPES.CODE,
  'php': FILE_TYPES.CODE,
  'phtml': FILE_TYPES.CODE,
  'php3': FILE_TYPES.CODE,
  'php4': FILE_TYPES.CODE,
  'php5': FILE_TYPES.CODE,
  'phps': FILE_TYPES.CODE,
  'yml': FILE_TYPES.CODE,
  'yaml': FILE_TYPES.CODE,
  'toml': FILE_TYPES.CODE,
  'ini': FILE_TYPES.CODE,
  'conf': FILE_TYPES.CODE,
  'dockerfile': FILE_TYPES.CODE,
  'sh': FILE_TYPES.CODE,
  'bash': FILE_TYPES.CODE,
  'zsh': FILE_TYPES.CODE,
  'fish': FILE_TYPES.CODE,
  'log': FILE_TYPES.CODE,
  'jsonl': FILE_TYPES.CODE,
  'ndjson': FILE_TYPES.CODE,
  'sql': FILE_TYPES.CODE,
  'diff': FILE_TYPES.CODE,
  'patch': FILE_TYPES.CODE,
  'vue': FILE_TYPES.CODE,
  'svelte': FILE_TYPES.CODE,
  'lua': FILE_TYPES.CODE,
  'r': FILE_TYPES.CODE,
  'rmd': FILE_TYPES.CODE,
  'dart': FILE_TYPES.CODE,
  'scala': FILE_TYPES.CODE,
  'sc': FILE_TYPES.CODE,
  'pl': FILE_TYPES.CODE,
  'pm': FILE_TYPES.CODE,
  'ex': FILE_TYPES.CODE,
  'exs': FILE_TYPES.CODE,
  'erl': FILE_TYPES.CODE,
  'hrl': FILE_TYPES.CODE,
  'hs': FILE_TYPES.CODE,
  'clj': FILE_TYPES.CODE,
  'cljs': FILE_TYPES.CODE,
  'cljc': FILE_TYPES.CODE,
  'edn': FILE_TYPES.CODE,
  'tf': FILE_TYPES.CODE,
  'tfvars': FILE_TYPES.CODE,
  'hcl': FILE_TYPES.CODE,
  'proto': FILE_TYPES.CODE,
  'graphql': FILE_TYPES.CODE,
  'gql': FILE_TYPES.CODE,
  'properties': FILE_TYPES.CODE,
  'cfg': FILE_TYPES.CODE,
  'env': FILE_TYPES.CODE,

  // Generic binary / archives (store-only)
  'pak': FILE_TYPES.FILE,
  'bin': FILE_TYPES.FILE,
  'dat': FILE_TYPES.FILE,
  'data': FILE_TYPES.FILE,
  'db': FILE_TYPES.FILE,
  'sqlite': FILE_TYPES.FILE,
  'sqlite3': FILE_TYPES.FILE,
  'sav': FILE_TYPES.FILE,
  'dump': FILE_TYPES.FILE,
  'gz': FILE_TYPES.FILE,
  'tgz': FILE_TYPES.FILE,
  'tar': FILE_TYPES.FILE,
  'rar': FILE_TYPES.FILE,
  '7z': FILE_TYPES.FILE,
  'bz2': FILE_TYPES.FILE,
  'xz': FILE_TYPES.FILE,
  'zst': FILE_TYPES.FILE,
  'wasm': FILE_TYPES.FILE,

  // Audio
  'mp3': FILE_TYPES.AUDIO,
  'wav': FILE_TYPES.AUDIO,
  'mid': FILE_TYPES.DOCUMENT,
  'midi': FILE_TYPES.DOCUMENT,

  // Video
  'mp4': FILE_TYPES.VIDEO,
  'mov': FILE_TYPES.VIDEO,
  'avi': FILE_TYPES.VIDEO,
  'webm': FILE_TYPES.VIDEO,
  'm4v': FILE_TYPES.VIDEO,
  'mkv': FILE_TYPES.VIDEO,

  // CAD
  'dwg': FILE_TYPES.CAD,
  'dxf': FILE_TYPES.CAD
};

export const getFileIconByExtension = (extension) => {
  if (!extension) return 'file';

  if (extension?.includes('.')) {
    extension = extension.split('.').pop().toLowerCase();
  }

  switch (extension?.toLowerCase()) {
    // Images
    case 'jpg':
    case 'jpeg':
      return 'file-jpg';
    case 'png':
      return 'file-png';
    case 'gif':
      return 'file-gif';
    case 'svg':
      return 'file-svg';
    case 'eps':
    case 'ai':
      return 'file-eps';
    case 'webp':
    case 'heic':
      return 'file-image';

    // Documents
    case 'pdf':
      return 'file-pdf';
    case 'doc':
    case 'docx':
      return 'file-word';
    case 'xls':
    case 'xlsx':
      return 'file-excel';
    case 'ppt':
    case 'pptx':
      return 'file-powerpoint';
    case 'csv':
    case 'tsv':
      return 'file-csv';
    case 'ods':
      return 'file-spreadsheet';
    case 'xml':
    case 'musicxml':
    case 'mxl':
      return 'file-xml';
    case 'mid':
    case 'midi':
      return 'file-audio';
    case 'md':
    case 'markdown':
      return 'file-text';
    case 'epub':
      return 'file-book';

    // Code files
    case 'txt':
    case 'html':
    case 'htm':
    case 'json':
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
    case 'py':
    case 'pyw':
    case 'ipynb':
    case 'rb':
    case 'erb':
    case 'gemfile':
    case 'java':
    case 'class':
    case 'jar':
    case 'c':
    case 'h':
    case 'cpp':
    case 'hpp':
    case 'cc':
    case 'hh':
    case 'cs':
    case 'go':
    case 'rs':
    case 'swift':
    case 'kt':
    case 'kts':
    case 'php':
    case 'phtml':
    case 'php3':
    case 'php4':
    case 'php5':
    case 'phps':
    case 'yml':
    case 'yaml':
    case 'toml':
    case 'ini':
    case 'conf':
    case 'dockerfile':
    case 'sh':
    case 'bash':
    case 'zsh':
    case 'fish':
    case 'css':
    case 'scss':
    case 'sass':
    case 'less':
    case 'sql':
    case 'diff':
    case 'patch':
    case 'vue':
    case 'svelte':
    case 'lua':
    case 'r':
    case 'rmd':
    case 'dart':
    case 'scala':
    case 'sc':
    case 'pl':
    case 'pm':
    case 'ex':
    case 'exs':
    case 'erl':
    case 'hrl':
    case 'hs':
    case 'clj':
    case 'cljs':
    case 'cljc':
    case 'edn':
    case 'tf':
    case 'tfvars':
    case 'hcl':
    case 'proto':
    case 'graphql':
    case 'gql':
    case 'properties':
    case 'cfg':
    case 'env':
    case 'wasm':
      return 'file-code';

    // Generic binary / archives
    case 'pak':
    case 'bin':
    case 'dat':
    case 'data':
    case 'db':
    case 'sqlite':
    case 'sqlite3':
    case 'sav':
    case 'dump':
      return 'file';
    case 'gz':
    case 'tgz':
    case 'tar':
    case 'rar':
    case '7z':
    case 'bz2':
    case 'xz':
    case 'zst':
      return 'file-zipper';

    // Audio
    case 'mp3':
      return 'file-mp3';
    case 'wav':
      return 'file-audio';

    // Video
    case 'mp4':
      return 'file-mp4';
    case 'mov':
      return 'file-mov';
    case 'avi':
    case 'webm':
      return 'file-video';

    // CAD and Design
    case 'dwg':
    case 'dxf':
      return 'file-cad';

    // Default for unknown types
    default:
      return 'file';
  }
};

export const getFileTypeName = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  const fileType = EXTENSION_MAP[extension];

  switch (fileType) {
    case FILE_TYPES.IMAGE:
      return 'Image';
    case FILE_TYPES.DOCUMENT:
      switch (extension) {
        case 'pdf':
          return 'PDF';
        case 'doc':
        case 'docx':
          return 'Word';
        case 'xls':
        case 'xlsx':
        case 'ods':
          return 'Spreadsheet';
        case 'ppt':
        case 'pptx':
          return 'Presentation';
        case 'csv':
        case 'tsv':
          return 'CSV';
        case 'md':
        case 'markdown':
          return 'Markdown';
        case 'epub':
          return 'eBook';
        default:
          return 'Document';
      }
    case FILE_TYPES.CODE:
      return 'Code';
    case FILE_TYPES.AUDIO:
      return 'Audio';
    case FILE_TYPES.VIDEO:
      return 'Video';
    case FILE_TYPES.CAD:
      return 'CAD';
    case FILE_TYPES.FILE:
      return 'File';
    default:
      return extension.toUpperCase();
  }
};

export const isImageFile = (extension) => {
  if (!extension) return false;
  
  if (extension?.includes('.')) {
    extension = extension.split('.').pop().toLowerCase();
  }
  return EXTENSION_MAP[extension?.toLowerCase()] === FILE_TYPES.IMAGE;
};

export const getStatusColor = (status) => {
  const statusColors = {
    'pending': { // good
      background: 'rgba(245, 124, 0, 0.13)',
      text: '#F57C00'
    },
    'processing': {
      background: 'rgba(255, 220, 10, 0.12)',
      text: 'rgba(150, 130, 10, 0.65)'
    },
    'completed': { // good
      background: 'rgba(0, 255, 0, 0.13)',
      text: 'rgb(35, 200, 45)'
    },
    'failed': { // good
      background: 'rgba(255, 40, 40, 0.12)',
      text: 'rgb(255, 40, 40)'
    },
    'skipped': { // good
      background: 'rgba(100, 0, 255, 0.1)',
      text: 'rgb(175, 115, 255)'
    },
  };

  return statusColors[status] || statusColors.pending;
};

export const getFileIconByType = (type) => {
  switch (type.toLowerCase()) {
    case FILE_TYPES.IMAGE:
      return 'file-image';
    case FILE_TYPES.AUDIO:
      return 'file-audio';
    case FILE_TYPES.VIDEO:
      return 'file-video';
    case FILE_TYPES.CODE:
      return 'file-code';
    case FILE_TYPES.CAD:
      return 'file-cad';
    case FILE_TYPES.DOCUMENT:
      return 'file-doc';
    default:
      return 'file';
  }
};

export const getFileIconByMimeType = (fileType) => {
  if (!fileType) return 'file';
  // Common MIME types and extensions mapping
  const fileTypes = {
    // Documents
    'application/pdf': 'file-pdf',
    'application/vnd.gab-ai.document+json': 'file-word',
    'application/msword': 'file-word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
    'application/vnd.ms-excel': 'file-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
    'application/vnd.ms-powerpoint': 'file-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
    'text/csv': 'file-csv',
    'text/tab-separated-values': 'file-csv',
    'text/xml': 'file-xml',
    'application/xml': 'file-xml',
    'application/vnd.recordare.musicxml+xml': 'file-xml',
    'application/vnd.recordare.musicxml': 'file-xml',
    'audio/midi': 'file-audio',
    'audio/x-midi': 'file-audio',
    'audio/mid': 'file-audio',
    'application/vnd.oasis.opendocument.spreadsheet': 'file-spreadsheet',
    'text/markdown': 'file-text',
    'application/epub+zip': 'file-book',
    'application/zip': 'file-zipper',
    'application/x-zip-compressed': 'file-zipper',
    
    // Images
    'image/svg+xml': 'file-svg',
    'image/png': 'file-png',
    'image/jpeg': 'file-jpg',
    'image/gif': 'file-gif',
    'application/postscript': 'file-eps',
    'image/webp': 'file-image',
    'image/heic': 'file-image',
    
    // Video
    'video/mp4': 'file-mp4',
    'video/quicktime': 'file-mov',
    'video/x-msvideo': 'file-video',
    'video/webm': 'file-video',
    
    // Audio
    'audio/mpeg': 'file-mp3',
    'audio/mp3': 'file-mp3',
    'audio/wav': 'file-audio',
    'audio/x-wav': 'file-audio',
    
    // Code and Data
    'text/plain': 'file-code',
    'text/html': 'file-code',
    'application/json': 'file-code',
    'application/javascript': 'file-code',
    'text/css': 'file-code',
    'text/x-python': 'file-code',
    'application/x-ruby': 'file-code',
    'text/x-java': 'file-code',
    'text/x-c': 'file-code',
    'text/x-c++': 'file-code',
    'text/x-csharp': 'file-code',
    'text/x-go': 'file-code',
    'text/x-rust': 'file-code',
    'text/x-swift': 'file-code',
    'text/x-kotlin': 'file-code',
    'text/x-php': 'file-code',
    'text/x-yaml': 'file-code',
    'text/x-toml': 'file-code',
    'text/x-ini': 'file-code',
    'text/x-dockerfile': 'file-code',
    'text/x-shellscript': 'file-code',
    'text/x-log': 'file-code',
    'application/x-ndjson': 'file-code',
    
    // CAD and Design
    'application/x-autocad': 'file-cad',
    'image/vnd.dwg': 'file-cad',
    'image/vnd.dxf': 'file-cad',
  };

  // Check by exact MIME type first
  if (fileTypes[fileType]) {
    return fileTypes[fileType];
  }

  // Check by file type category
  if (fileType.startsWith('image/')) {
    return 'file-image';
  }
  
  if (fileType.startsWith('video/')) {
    return 'file-video';
  }
  
  if (fileType.startsWith('audio/')) {
    return 'file-audio';
  }

  if (fileType.startsWith('text/') || fileType.includes('code')) {
    return 'file-code';
  }

  // Default to generic file icon
  return 'file';
};

export const getFileTypeLabel = (fileType) => {
  if (!fileType) return 'Document';

  if (fileType.startsWith('image/')) return 'Image';
  if (fileType.startsWith('video/')) return 'Video';
  if (fileType.startsWith('audio/')) return 'Audio';
  
  const documentTypes = {
    'application/pdf': 'PDF Document',
    'application/vnd.gab-ai.document+json': 'Editable Document',
    'application/msword': 'Word Document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
    'application/vnd.ms-excel': 'Excel Spreadsheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'application/vnd.oasis.opendocument.spreadsheet': 'Spreadsheet',
    'application/epub+zip': 'eBook',
    'application/vnd.recordare.musicxml+xml': 'MusicXML',
    'application/vnd.recordare.musicxml': 'MusicXML',
    'audio/midi': 'MIDI',
    'audio/x-midi': 'MIDI',
    'audio/mid': 'MIDI',
    'application/zip': 'ZIP Archive',
    'application/x-zip-compressed': 'ZIP Archive',
    'text/csv': 'CSV File',
    'text/tab-separated-values': 'TSV File',
    'text/plain': 'Text Document',
    'text/markdown': 'Markdown Document',
    'application/json': 'JSON File',
    'text/html': 'HTML File',
    'text/css': 'CSS File',
    'application/javascript': 'JavaScript File',
    'text/x-python': 'Python File',
    'application/x-ruby': 'Ruby File',
    'text/x-java': 'Java File',
    'text/x-c': 'C File',
    'text/x-c++': 'C++ File',
    'text/x-csharp': 'C# File',
    'text/x-go': 'Go File',
    'text/x-rust': 'Rust File',
    'text/x-swift': 'Swift File',
    'text/x-kotlin': 'Kotlin File',
    'text/x-php': 'PHP File',
    'text/x-yaml': 'YAML File',
    'text/x-toml': 'TOML File',
    'text/x-ini': 'INI File',
    'text/x-dockerfile': 'Dockerfile',
    'text/x-shellscript': 'Shell Script',
    'application/x-autocad': 'CAD File',
    'image/vnd.dwg': 'CAD File',
    'image/vnd.dxf': 'CAD File',
  };

  return documentTypes[fileType] || 'Document';
};

export const getFileTypeColor = (fileType) => {
  if (!fileType) return '#9E9E9E';

  // Define color palette for different file types
  const typeColors = {
    'image': '#FF6B6B',  // Coral red for images
    'video': '#4ECDC4',  // Turquoise for videos
    'audio': '#45B7D1',  // Light blue for audio
    'pdf': '#FF7043',    // Deep orange for PDFs
    'word': '#4285F4',   // Microsoft blue for Word
    'excel': '#0F9D58',  // Green for Excel
    'powerpoint': '#DB4437', // Red for PowerPoint
    'text': '#673AB7',   // Purple for text files
    'code': '#2196F3',   // Blue for code files
    'cad': '#795548',    // Brown for CAD files
    'default': '#9E9E9E' // Grey for others
  };

  if (fileType.startsWith('image/')) return typeColors.image;
  if (fileType.startsWith('video/')) return typeColors.video;
  if (fileType.startsWith('audio/')) return typeColors.audio;
  if (fileType.includes('pdf')) return typeColors.pdf;
  if (fileType.includes('word')) return typeColors.word;
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return typeColors.excel;
  if (fileType.includes('powerpoint')) return typeColors.powerpoint;
  if (fileType.startsWith('text/')) return typeColors.text;
  if (fileType.includes('json') || fileType.includes('javascript') || fileType.includes('css') || fileType.includes('html')) return typeColors.code;
  if (fileType.includes('cad') || fileType.includes('dwg') || fileType.includes('dxf')) return typeColors.cad;
  
  return typeColors.default;
};

export const getStatusText = (status) => {
  const statusMap = {
    'processing': 'Processing',
    'completed': 'Complete',
    'failed': 'Failed',
    'pending': 'Pending',
    'skipped': 'Skipped',
    'uploading': 'Uploading'
  };
  return statusMap[status] || status;
};

let tempFileSequence = 0;

export const getTemporaryFileId = (fileName) => {
  tempFileSequence = (tempFileSequence + 1) % 1000000;
  return `uploading_${Date.now()}_${tempFileSequence}_${fileName}`;
};

/**
 * Returns true when the lightbox can render this file directly
 * (image, video, audio, and models on Mac). Documents are handled by the
 * artifact viewers rather than the lightbox.
 */
export const isModel3DFile = (file) => {
  if (!file) return false;
  const mime = String(file.mimeType || file.type || '').toLowerCase();
  return file.fileType === '3d' || file.type === '3d' || mime.startsWith('model/')
    || /\.(?:glb|gltf|obj|stl|ply|fbx|usdz?)(?:[?#].*)?$/i.test(file.cdnPath || file.url || '')
    || /\.(?:glb|gltf|obj|stl|ply|fbx|usdz?)$/i.test(file.name || '')
    || /^(?:glb|gltf|obj|stl|ply|fbx|usdz?)$/i.test(String(file.extension || '').replace(/^\./, ''));
};

export const isLightboxCompatibleFile = (file) => {
  if (!file) return false;
  const mime = (file.mimeType || file.type || '').toLowerCase();
  const fileType = file.fileType;
  if (fileType === 'image' || fileType === 'video' || fileType === 'audio') return true;
  if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) return true;
  // The Mac lightbox already hosts the native WebView model viewer. Files
  // must route models there instead of opening a generic file-details modal.
  if (Platform.OS === 'macos' && isModel3DFile(file)) return true;
  return false;
};

export const isPdfFile = (file) => {
  if (!file) return false;
  const mime = (file.mimeType || file.type || '').toLowerCase();
  const extension = (file.extension || '').toLowerCase();
  const name = (file.name || file.originalName || '').toLowerCase();
  return mime === 'application/pdf' || extension === '.pdf' || extension === 'pdf' || name.endsWith('.pdf');
};

export const isPresentationFile = (file) => {
  if (!file) return false;
  const mime = (file.mimeType || file.type || '').toLowerCase();
  const extension = (file.extension || '').toLowerCase();
  const name = (file.name || file.originalName || '').toLowerCase();
  return mime === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    || extension === '.pptx'
    || extension === 'pptx'
    || name.endsWith('.pptx');
};

export const isSpreadsheetFile = (file) => {
  if (!file) return false;
  const mime = (file.mimeType || file.type || '').toLowerCase();
  const extension = (file.extension || '').toLowerCase();
  const name = (file.name || file.originalName || '').toLowerCase();
  return mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    || extension === '.xlsx'
    || extension === 'xlsx'
    || name.endsWith('.xlsx');
};

export const isWordDocumentFile = (file) => {
  if (!file) return false;
  const mime = (file.mimeType || file.type || '').toLowerCase();
  const extension = (file.extension || '').toLowerCase();
  const name = (file.name || file.originalName || '').toLowerCase();
  return mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || extension === '.docx'
    || extension === 'docx'
    || name.endsWith('.docx');
};

export const isCsvFile = (file) => {
  if (!file) return false;
  const mime = (file.mimeType || file.type || '').toLowerCase();
  const extension = (file.extension || '').toLowerCase();
  const name = (file.name || file.originalName || '').toLowerCase();
  return mime === 'text/csv'
    || extension === '.csv'
    || extension === 'csv'
    || name.endsWith('.csv');
};

export const isTsvFile = (file) => {
  if (!file) return false;
  const mime = (file.mimeType || file.type || '').toLowerCase();
  const extension = (file.extension || '').toLowerCase().replace(/^\./, '');
  const name = (file.name || file.originalName || '').toLowerCase();
  return mime === 'text/tab-separated-values' || extension === 'tsv' || name.endsWith('.tsv');
};

/**
 * Uploaded office files that can be converted into an editable in-app copy
 * (docx/csv always; xlsx/pptx only when they don't already carry an
 * editable spec — generated artifacts open in the editor directly).
 */
export const isEditorImportableFile = (file) => {
  if (!file) return false;
  if (isWordDocumentFile(file) || isCsvFile(file) || isTsvFile(file)) return true;
  if (isSpreadsheetFile(file) && !file?.parameters?.spreadsheetSpec) return true;
  if (isPresentationFile(file) && !file?.parameters?.presentationSpec) return true;
  return false;
};

// Keep in sync with XS_BREAKPOINT in hooks/useResponsiveness.js (the
// ContextualSidebar that hosts every editor returns null at or below it).
const EDITOR_MIN_VIEWPORT_WIDTH = 768;

/**
 * The document / presentation / spreadsheet editors are desktop-web-only.
 * They need a wide viewport for the chat + editor split view and
 * mouse-driven interactions (drag/resize handles, hover toolbars,
 * contentEditable menus), so phones, iPads, and other touch-first tablets
 * get the plain file download / preview experience instead.
 */
const isWideWebPreview = (type) => (
  Platform.OS === 'web'
  && typeof window !== 'undefined'
  && (type
    ? canFitContextSidebar(type, window.innerWidth, isArtifactSidebarType(type) ? getCollapsedNavigationSidebarWidth() : undefined)
    : window.innerWidth >= EDITOR_MIN_VIEWPORT_WIDTH)
);

const canOpenVideoStudio = () => Platform.OS === 'macos' || isWideWebPreview('video-project-preview');

export const canUseDesktopEditors = () => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  if (window.innerWidth < EDITOR_MIN_VIEWPORT_WIDTH) return false;
  const nav = window.navigator || {};
  const ua = nav.userAgent || '';
  // Phones and classic tablet user agents.
  if (/iPad|iPhone|iPod|Android/i.test(ua)) return false;
  // iPadOS 13+ Safari masquerades as macOS but reports multi-touch.
  if (/Macintosh/i.test(ua) && (nav.maxTouchPoints || 0) > 1) return false;
  // Any remaining touch-primary device without a mouse/trackpad.
  try {
    if (window.matchMedia('(pointer: coarse)').matches &&
        !window.matchMedia('(any-pointer: fine)').matches) {
      return false;
    }
  } catch (_) { /* matchMedia unavailable — assume capable */ }
  return true;
};

export const openEditorSidebar = (sidebarType, file) => {
  // Width comes from the store, which sizes each sidebar type on open.
  useUIStore.getState().openContextSidebar(sidebarType, { file });
};

/**
 * Create a document/spreadsheet/presentation (optionally seeded) and open it
 * in the contextual sidebar. No-ops on phones / native where editors are off.
 *
 * @param {'document'|'spreadsheet'|'presentation'} type
 * @param {{ markdown?: string, rows?: string[][], title?: string, conversationId?: string, messageId?: string }} [opts]
 */
export const createAndOpenEditor = async (type, opts = {}) => {
  if (!canUseDesktopEditors()) {
    throw new Error('Desktop editors are not available on this device');
  }
  // Deferred import avoids a files.js <-> api.js module cycle.
  const { filesApi } = await import('@/lib/api');
  const { editor, file } = await filesApi.createEditorFile({
    type,
    markdown: opts.markdown,
    rows: opts.rows,
    title: opts.title,
    conversationId: opts.conversationId,
    messageId: opts.messageId,
  });
  const sidebarType = editor === 'spreadsheet'
    ? 'spreadsheet-editor'
    : (editor === 'presentation' ? 'presentation-editor' : 'document-editor');
  openEditorSidebar(sidebarType, file);
  return file;
};

const explainOfficePreviewFallback = (error) => {
  const detail = typeof error?.message === 'string' && error.message.includes('the editor safely supports up to')
    ? `${error.message} ` : '';
  useToastStore.getState().actions.addToast(`${detail}Preview unavailable. Opening the original file instead.`, 'info');
};

/**
 * Import an uploaded office file into an editable copy on the server, then
 * open the matching editor. Falls back to opening the raw file in a new tab
 * if the import fails.
 */
const importFileIntoEditor = async (file, opts = {}) => {
  const shouldOpen = createFileOpenGuard(opts.shouldOpen);
  if (!shouldOpen()) return;
  const fileId = file.id || file._id;
  try {
    // Deferred import avoids a files.js <-> api.js module cycle.
    const { filesApi } = await import('@/lib/api');
    if (!shouldOpen()) return;
    const { editor, file: editorFile } = await filesApi.openFileInEditor(fileId, { isRequestCurrent: shouldOpen });
    const sidebarType = editor === 'spreadsheet'
      ? 'spreadsheet-editor'
      : (editor === 'presentation' ? 'presentation-editor' : 'document-editor');
    if (!shouldOpen()) return;
    useUIStore.getState().openContextSidebar(sidebarType, { file: editorFile, sourceFile: file });
  } catch (error) {
    if (!shouldOpen()) return;
    if (opts.throwOnError) throw error;
    if (opts.recoverExistingSpreadsheet) {
      openEditorSidebar('spreadsheet-editor', file);
      return;
    }
    console.warn('[files] Failed to import file into editor, opening raw file instead', error);
    explainOfficePreviewFallback(error);
    if (typeof window !== 'undefined') {
      window.open(file.cdnPath, '_blank', 'noopener,noreferrer');
    }
  }
};

/**
 * Import a raw office file into an editable copy on the server and show it in
 * the matching read-only viewer — the mobile counterpart of
 * `importFileIntoEditor`. This lets Work-created .xlsx files open as actual
 * workbooks instead of opaque CDN downloads. Best effort: any failure falls
 * back to the OS hand-off.
 */
const importOfficeFileIntoViewer = async (file, opts = {}) => {
  const shouldOpen = createFileOpenGuard(opts.shouldOpen);
  if (!shouldOpen()) return;
  const fileId = file.id || file._id;
  if (!fileId) {
    openFileExternally(file);
    return;
  }
  try {
    // Deferred load avoids the files.js <-> api.js module cycle while keeping
    // native file routing independent of an additional development chunk load.
    const { filesApi } = require('@/lib/api');
    const { file: editorFile } = await filesApi.openFileInEditor(fileId, { isRequestCurrent: shouldOpen });
    if (!shouldOpen()) return;
    if (openArtifactViewerForFile(editorFile)) return;
    openFileExternally(file);
  } catch (error) {
    if (!shouldOpen()) return;
    if (opts.recoverExistingSpreadsheet && openArtifactViewerForFile(file)) return;
    console.warn('[files] Failed to import office file for viewing, opening it externally instead', error);
    explainOfficePreviewFallback(error);
    openFileExternally(file);
  }
};

export function getOpenVideoProjectProps(conversationId) {
  const ui = useUIStore.getState();
  const viewer = useArtifactViewerStore.getState();
  const props = Platform.OS === 'macos' && viewer.isOpen && viewer.kind === 'video-project'
    ? viewer.file
    : ui.isContextSidebarVisible && ui.contextSidebarType === 'video-project-preview'
      ? ui.contextSidebarProps : null;
  return props && (!conversationId || String(props.conversationId || '') === String(conversationId)) ? props : null;
}

export function maybeAutoOpenVideoProject(opts = {}) {
  if (!canOpenVideoStudio()) return false;
  const openProps = getOpenVideoProjectProps(opts.conversationId);

  if (opts.onlyIfOpen && !openProps) return false;

  // Opening the studio starts at the latest finished video. Background refreshes
  // keep the user's current preview, including an intentionally opened old cut.
  const preserveSelection = !!opts.onlyIfOpen && !opts.file;
  const currentFile = preserveSelection ? openProps?.file : null;
  const film = !opts.file && !currentFile ? buildVideoProject(opts).film : null;
  const file = opts.file || currentFile || film?.source || film || firstVideoProjectMedia(opts);
  if (!file && !(opts.videoProject === true && opts.taskId && opts.conversationId)) return false;

  return tryOpenVideoProject(file, {
    ...opts,
    preserveSelection,
  });
}

function tryOpenVideoProject(file, opts = {}) {
  if (!canOpenVideoStudio()) return false;
  const emptyProject = !file && opts.videoProject === true && opts.taskId && opts.conversationId;
  if (!emptyProject && (!file || !isGeneratedVideoProjectFile(file, opts))) return false;

  let ctx = (typeof opts.videoProject === 'object' ? opts.videoProject : null) || useUIStore.getState().videoProjectContext || null;
  if (opts.taskId && ctx?.taskId && String(opts.taskId) !== String(ctx.taskId)) ctx = null;
  if (opts.conversationId && ctx?.conversationId
    && String(opts.conversationId) !== String(ctx.conversationId)) {
    ctx = null;
  }
  if (ctx && !opts.artifacts && !opts.files && !opts.scriptText
    && !fileBelongsToVideoProject(file, ctx)) {
    ctx = null;
  }

  const ui = useUIStore.getState();
  const selectionRequest = Number(getOpenVideoProjectProps()?.selectionRequest) || 0;
  const nextProps = {
    file,
    artifacts: opts.artifacts || ctx?.artifacts || null,
    files: opts.files || ctx?.files || null,
    scriptText: opts.scriptText || ctx?.scriptText || '',
    taskId: opts.taskId || ctx?.taskId || null,
    conversationId: opts.conversationId || ctx?.conversationId || null,
    selectedId: opts.selectedId || null,
    selectionRequest: opts.preserveSelection ? selectionRequest : selectionRequest + 1,
  };
  const viewer = useArtifactViewerStore.getState();
  if (Platform.OS === 'macos' && ((viewer.isOpen && viewer.kind === 'video-project')
    || !canFitContextSidebar('video-project-preview', Dimensions.get('window').width, getCollapsedNavigationSidebarWidth()))) {
    if (ui.isContextSidebarVisible && ui.contextSidebarType === 'video-project-preview') ui.closeContextSidebar();
    viewer.openArtifactViewer('video-project', nextProps);
    return true;
  }
  if (ui.isContextSidebarVisible && ui.contextSidebarType === 'video-project-preview') {
    ui.setContextSidebarProps(nextProps);
    return true;
  }
  ui.openContextSidebar('video-project-preview', nextProps);
  return true;
}

/**
 * Open files consistently from chat and Files. Standalone videos use a
 * focused player; explicit production files open their studio.
 * Docs, decks, sheets, PDFs, and source files open in the matching
 * in-app viewer or editor. Everything else opens in a new tab on web
 * or hands off to the OS on native.
 *
 * @param {object} file - File-like object with at least cdnPath + id
 *   (Work video artifacts may open with path + taskId instead)
 * @param {object} opts
 * @param {function} [opts.onOpenLightbox] - Lightbox opener (image/video only)
 * @param {boolean} [opts.openToEditor=false] - Forwarded to lightbox
 * @param {boolean} [opts.withInpainting=false] - Forwarded to lightbox
 * @param {boolean} [opts.withChatInput=false] - Forwarded to lightbox
 */
export const openMessageFile = (file, opts = {}) => {
  if (opts.shouldOpen && !opts.shouldOpen()) return;
  if (isVideoMedia(file)) {
    if ((opts.videoProject === true || (opts.videoProject !== false && isStudioVideo(file)))
      && tryOpenVideoProject(file, { ...opts, videoProject: true, taskId: opts.taskId || file.taskId || file.parameters?.workTaskId, conversationId: opts.conversationId || file.conversationId })) return;
    const previewFile = {
      ...file,
      taskId: opts.taskId || file.taskId || file.parameters?.workTaskId || null,
      path: file.path || file.parameters?.workArtifactPath || null,
      conversationId: opts.conversationId || file.conversationId || null,
    };
    if (isWideWebPreview('video-preview')) {
      useUIStore.getState().openContextSidebar('video-preview', { file: previewFile });
    } else {
      useArtifactViewerStore.getState().openArtifactViewer('video', previewFile);
    }
    return;
  }
  if (tryOpenVideoProject(file, opts)) return;

  if (isImageArtifact(file) && (file?.cdnPath || file?.url || file?.uri) && canUseDesktopEditors()) {
    openEditorSidebar('image-editor', opts.conversationId ? { ...file, conversationId: opts.conversationId } : file);
    return;
  }

  if (!file?.cdnPath) return;

  if (isSpreadsheetFile(file) && mayNeedSpreadsheetImportRecovery(file)) {
    const recoveryOptions = { ...opts, recoverExistingSpreadsheet: true };
    return canUseDesktopEditors() ? importFileIntoEditor(file, recoveryOptions) : importOfficeFileIntoViewer(file, recoveryOptions);
  }

  // Editors are desktop-web-only; on phones / tablets / native these
  // branches fall through to the plain download / new-tab behavior below.
  if (isEditableDocumentFile(file) && canUseDesktopEditors()) {
    openEditorSidebar('document-editor', file);
    return;
  }

  if (isPresentationFile(file) && file?.parameters?.presentationSpec && canUseDesktopEditors()) {
    openEditorSidebar('presentation-editor', file);
    return;
  }

  if (isSpreadsheetFile(file) && file?.parameters?.spreadsheetSpec && canUseDesktopEditors()) {
    openEditorSidebar('spreadsheet-editor', file);
    return;
  }

  // Preview the original deck even when Work has persisted it as a File.
  // Automatic editable import can discard charts and authored styling.
  // Conversion is an explicit editing action, not the default preview.
  if (isPresentationFile(file) && !file?.parameters?.presentationSpec && !opts.openToEditor) {
    if (isWideWebPreview('pptx-preview')) {
      useUIStore.getState().openContextSidebar('pptx-preview', { file });
      return;
    }
    if (openArtifactViewerForFile(file)) return;
  }

  // Uploaded office files (docx/xlsx/csv/pptx without an editable spec)
  // are converted server-side into an editable copy and opened in the
  // matching editor.
  if (isEditorImportableFile(file) && canUseDesktopEditors()) {
    return importFileIntoEditor(file, opts);
  }

  // Raw Word / spreadsheet files on phones, touch tablets, and native: run
  // the same server-side import the desktop editor uses, then show the
  // editable copy in the matching read-only viewer.
  if (!canUseDesktopEditors()
    && (isWordDocumentFile(file)
      || (isSpreadsheetFile(file) && !file?.parameters?.spreadsheetSpec)
      || isCsvFile(file) || isTsvFile(file))) {
    return importOfficeFileIntoViewer(file, opts);
  }

  // The PDF preview sidebar is view-only so touch devices are fine, but the
  // sidebar doesn't render on phone-sized viewports — fall through to the
  // browser's native PDF handling there instead of a dead tap.
  if (isPdfFile(file) && isWideWebPreview('pdf-preview')) {
    if (typeof opts.onOpenDocumentPreview === 'function') {
      opts.onOpenDocumentPreview(file);
    } else {
      useUIStore.getState().openContextSidebar('pdf-preview', { file });
    }
    return;
  }

  // Code is view-only like PDFs: wide web gets the sidebar, everything else
  // uses the full-screen artifact viewer instead of dumping raw source.
  if (isCodeFile(file) && isWideWebPreview('code-preview')) {
    useUIStore.getState().openContextSidebar('code-preview', { file });
    return;
  }

  if (isLightboxCompatibleFile(file) && typeof opts.onOpenLightbox === 'function') {
    opts.onOpenLightbox({
      imageUrl: file.cdnPath,
      fileId: file.id,
      openToEditor: opts.openToEditor === true,
      withInpainting: opts.withInpainting === true,
      withChatInput: opts.withChatInput === true,
    });
    return;
  }

  // Reaching here with an editable spec means the editors were ruled out
  // above, so this is a phone, a touch tablet, or native. Show the read-only
  // viewer rather than making the user leave the app to see what the AI made.
  if (openArtifactViewerForFile(file)) return;

  openFileExternally(file);
};

/**
 * After an upload finishes on desktop web, open the file in its matching
 * editor when we have one: docx/csv (and spec-less xlsx/pptx) go through the
 * server-side import into an editable copy; editable artifacts open directly.
 * @returns {boolean} whether an editor (or the import flow) took the file
 */
export const maybeOpenUploadedFileInEditor = (file) => {
  if (!file || !canUseDesktopEditors()) return false;
  const editorSupported = isEditableDocumentFile(file)
    || (isPresentationFile(file) && file?.parameters?.presentationSpec)
    || (isSpreadsheetFile(file) && file?.parameters?.spreadsheetSpec)
    || isEditorImportableFile(file);
  if (!editorSupported) return false;
  openMessageFile(file);
  return true;
};

/**
 * Click handler for a file the user attached. Editors still auto-open for
 * docs/decks/sheets; images open Image studio and source files open the code preview. Everything else
 * should fall through to the file-details modal.
 */
export const openUploadedChatFile = (file, opts = {}) => {
  if (!file) return false;
  if (isVideoMedia(file)) { openMessageFile(file, opts); return true; }
  if (isImageArtifact(file) && (file.cdnPath || file.url || file.uri) && canUseDesktopEditors()) {
    openMessageFile(file, opts);
    return true;
  }
  if (isCodeFile(file) && file.cdnPath) {
    openMessageFile(file, opts);
    return true;
  }
  return maybeOpenUploadedFileInEditor(file);
};

/**
 * Open the read-only artifact viewer if the file carries something we can
 * render: a presentation/spreadsheet spec, a raw .pptx (parsed in the
 * viewer), or an editable document. Document Markdown is intentionally
 * omitted from File.serialize() (payload size), so DocumentViewer loads it
 * via GET /json/files/:id/document when needed.
 * @returns {boolean} whether the viewer took the file
 */
export const openArtifactViewerForFile = (file) => {
  let kind = null;
  if (isVideoMedia(file)) {
    kind = 'video';
  } else if (isPresentationFile(file) && file?.parameters?.presentationSpec) {
    kind = 'presentation';
  } else if (isPresentationFile(file) && (file.cdnPath || file.url)) {
    kind = 'pptx';
  } else if (isSpreadsheetFile(file) && file?.parameters?.spreadsheetSpec) {
    kind = 'spreadsheet';
  } else if (isEditableDocumentFile(file)) {
    kind = 'document';
  } else if (isCodeFile(file)) {
    kind = 'code';
  } else if (isPdfFile(file) && (file.cdnPath || file.url) && Platform.OS !== 'web') {
    // Native only: the in-app viewer renders PDFs itself (WKWebView / pdf.js).
    // Narrow web keeps the browser hand-off — mobile browsers either refuse
    // to render PDFs in a frame (Android Chrome) or do it badly (Safari).
    kind = 'pdf';
  }
  if (!kind) return false;
  useArtifactViewerStore.getState().openArtifactViewer(kind, file);
  return true;
};

/** Hand a file to the browser (new tab) or to the OS on native. */
export const openFileExternally = (file) => {
  const url = file?.cdnPath || file?.url || file?.uri;
  if (!url || typeof url !== 'string') return;
  const trimmed = url.trim();
  const isHttp = /^https?:\/\//i.test(trimmed);
  const isWebRelative = Platform.OS === 'web' && trimmed.startsWith('/') && !trimmed.startsWith('//');
  if (!isHttp && !isWebRelative) return;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(trimmed, '_blank', 'noopener,noreferrer');
  } else {
    Linking.openURL(trimmed).catch(() => {});
  }
};


export const getFileTypeCategory = (file) => {
  const mimeType = file.mimeType || file.type || '';
  const name = file.name || '';
  const ext = (file.extension || name.split('.').pop() || '').replace(/^\./, '').toLowerCase();
  
  // Documents
  if (mimeType.includes('pdf') || mimeType.includes('word') || mimeType.includes('document') ||
      file.fileType === 'document' || file.fileType === 'spreadsheet' ||
      ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'tsv', 'ods', 'odp', 'md', 'markdown', 'epub'].includes(ext)) {
    return 'documents';
  }
  // Images
  if (file.fileType === 'image' || mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return 'images';
  }
  // Audio
  if (file.fileType === 'audio' || mimeType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(ext)) {
    return 'audio';
  }
  // Video
  if (file.fileType === 'video' || mimeType.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv'].includes(ext)) {
    return 'video';
  }
  // Code
  if (file.fileType === 'code' || ['js', 'jsx', 'ts', 'tsx', 'py', 'rb', 'java', 'c', 'cpp', 'h', 'cs', 'go', 'rs', 'php', 'html', 'css', 'scss', 'json', 'xml', 'yaml', 'yml', 'md', 'sh', 'sql'].includes(ext)) {
    return 'code';
  }
  return 'other';
};
